import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  FileText,
  Plus,
  Euro,
  Send,
  CreditCard,
  Download,
  Eye,
  Mail,
} from 'lucide-react';

const STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
  open: { label: 'Ouverte', color: 'bg-blue-100 text-blue-700' },
  sent: { label: 'Envoyée', color: 'bg-amber-100 text-amber-700' },
  paid: { label: 'Payée', color: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-700' },
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [approvedCRAs, setApprovedCRAs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedCRA, setSelectedCRA] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, crasRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/cra'),
      ]);
      setInvoices(invoicesRes.data);
      // Filter CRAs that are approved and don't have an invoice yet
      const invoicedCRAIds = invoicesRes.data.map(inv => inv.cra_id);
      setApprovedCRAs(crasRes.data.filter(cra => 
        cra.status === 'approved' && !invoicedCRAIds.includes(cra.cra_id)
      ));
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!selectedCRA) {
      toast.error('Veuillez sélectionner un CRA');
      return;
    }

    try {
      await api.post('/invoices', {
        cra_id: selectedCRA,
        vat_rate: 20,
        payment_terms: '30 jours à réception de facture',
      });
      toast.success('Facture créée avec succès');
      setIsCreateDialogOpen(false);
      setSelectedCRA('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateStatus = async (invoiceId, status) => {
    try {
      const payload = { status };
      if (status === 'paid') {
        payload.payment_date = new Date().toISOString();
      }
      await api.put(`/invoices/${invoiceId}`, payload);
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const openPreview = (invoice) => {
    setSelectedInvoice(invoice);
    setIsPreviewOpen(true);
  };

  // Calculate totals
  const totals = {
    draft: invoices.filter(i => i.status === 'draft').reduce((sum, i) => sum + i.amount_ttc, 0),
    pending: invoices.filter(i => ['open', 'sent'].includes(i.status)).reduce((sum, i) => sum + i.amount_ttc, 0),
    paid: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.amount_ttc, 0),
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Factures</h1>
            <p className="text-slate-500 mt-1">
              Gérez et suivez vos factures clients
            </p>
          </div>
          <Button
            className="bg-slate-900 hover:bg-slate-800"
            onClick={() => setIsCreateDialogOpen(true)}
            disabled={approvedCRAs.length === 0}
            data-testid="create-invoice-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Générer une facture
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {formatCurrency(totals.draft)}
                  </p>
                  <p className="text-sm text-slate-500">Brouillons</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Send className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {formatCurrency(totals.pending)}
                  </p>
                  <p className="text-sm text-slate-500">En attente</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {formatCurrency(totals.paid)}
                  </p>
                  <p className="text-sm text-slate-500">Payées</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Invoices Table */}
        <Card>
          <CardHeader>
            <CardTitle>Liste des factures</CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucune facture pour le moment</p>
                <p className="text-sm text-slate-400 mt-1">
                  Les factures sont générées à partir des CRA approuvés
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N° Facture</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Mission</TableHead>
                      <TableHead>Période</TableHead>
                      <TableHead className="text-center">Jours</TableHead>
                      <TableHead className="text-right">Montant HT</TableHead>
                      <TableHead className="text-right">Montant TTC</TableHead>
                      <TableHead className="text-center">Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.invoice_id} data-testid={`invoice-row-${invoice.invoice_id}`}>
                        <TableCell className="font-mono font-medium">
                          {invoice.invoice_number}
                        </TableCell>
                        <TableCell>{invoice.client_name}</TableCell>
                        <TableCell>{invoice.mission_title}</TableCell>
                        <TableCell>
                          {invoice.month && invoice.year && (
                            format(new Date(invoice.year, invoice.month - 1), 'MMMM yyyy', { locale: fr })
                          )}
                        </TableCell>
                        <TableCell className="text-center font-mono">
                          {invoice.total_days?.toFixed(1)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(invoice.amount_ht)}
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          {formatCurrency(invoice.amount_ttc)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={STATUS_CONFIG[invoice.status]?.color}>
                            {STATUS_CONFIG[invoice.status]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openPreview(invoice)}
                              data-testid={`preview-invoice-${invoice.invoice_id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const apiUrl = process.env.REACT_APP_BACKEND_URL;
                                window.open(`${apiUrl}/api/invoices/${invoice.invoice_id}/pdf`, '_blank');
                              }}
                              data-testid={`download-invoice-${invoice.invoice_id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {invoice.status === 'draft' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateStatus(invoice.invoice_id, 'open')}
                                data-testid={`open-invoice-${invoice.invoice_id}`}
                              >
                                Ouvrir
                              </Button>
                            )}
                            {invoice.status === 'open' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateStatus(invoice.invoice_id, 'sent')}
                                data-testid={`send-invoice-${invoice.invoice_id}`}
                              >
                                <Send className="w-4 h-4 mr-1" />
                                Envoyer
                              </Button>
                            )}
                            {invoice.status === 'sent' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    try {
                                      await api.post(`/invoices/${invoice.invoice_id}/send-reminder`);
                                      toast.success('Rappel envoyé');
                                    } catch (error) {
                                      toast.info('Email non configuré (SendGrid requis)');
                                    }
                                  }}
                                  data-testid={`reminder-invoice-${invoice.invoice_id}`}
                                >
                                  <Mail className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => handleUpdateStatus(invoice.invoice_id, 'paid')}
                                  data-testid={`mark-paid-${invoice.invoice_id}`}
                                >
                                  <CreditCard className="w-4 h-4 mr-1" />
                                  Payée
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Invoice Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Générer une facture</DialogTitle>
              <DialogDescription>
                Sélectionnez un CRA approuvé pour générer la facture correspondante
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Select value={selectedCRA} onValueChange={setSelectedCRA}>
                <SelectTrigger data-testid="invoice-cra-select">
                  <SelectValue placeholder="Sélectionner un CRA" />
                </SelectTrigger>
                <SelectContent>
                  {approvedCRAs.map((cra) => (
                    <SelectItem key={cra.cra_id} value={cra.cra_id}>
                      {cra.mission_title} - {format(new Date(cra.year, cra.month - 1), 'MMMM yyyy', { locale: fr })} ({cra.total_days} jours)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleCreateInvoice} className="bg-slate-900 hover:bg-slate-800" data-testid="confirm-create-invoice-btn">
                Générer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invoice Preview Dialog */}
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Aperçu de la facture</DialogTitle>
            </DialogHeader>
            {selectedInvoice && (
              <div className="bg-white p-8 border rounded-lg">
                {/* Invoice Header */}
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold">OC</span>
                      </div>
                      <span className="font-bold text-xl text-slate-900">Omaz Consulting</span>
                    </div>
                    <p className="text-sm text-slate-500">123 Avenue des Champs-Élysées</p>
                    <p className="text-sm text-slate-500">75008 Paris, France</p>
                    <p className="text-sm text-slate-500">SIRET: 123 456 789 00012</p>
                    <p className="text-sm text-slate-500">TVA: FR12345678901</p>
                  </div>
                  <div className="text-right">
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">FACTURE</h2>
                    <p className="font-mono text-lg">{selectedInvoice.invoice_number}</p>
                    <p className="text-sm text-slate-500 mt-2">
                      Date: {format(new Date(selectedInvoice.issue_date), 'dd/MM/yyyy')}
                    </p>
                    <p className="text-sm text-slate-500">
                      Échéance: {format(new Date(selectedInvoice.due_date), 'dd/MM/yyyy')}
                    </p>
                  </div>
                </div>

                {/* Client Info */}
                <div className="mb-8 p-4 bg-slate-50 rounded-lg">
                  <p className="font-medium text-slate-900">{selectedInvoice.client_name}</p>
                </div>

                {/* Invoice Details */}
                <table className="w-full mb-8">
                  <thead>
                    <tr className="border-b-2 border-slate-200">
                      <th className="text-left py-2 font-medium text-slate-700">Description</th>
                      <th className="text-center py-2 font-medium text-slate-700">Qté</th>
                      <th className="text-right py-2 font-medium text-slate-700">Prix unit.</th>
                      <th className="text-right py-2 font-medium text-slate-700">Total HT</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-100">
                      <td className="py-3">
                        <p className="font-medium">{selectedInvoice.mission_title}</p>
                        <p className="text-sm text-slate-500">
                          Consultant: {selectedInvoice.consultant_name}
                        </p>
                        <p className="text-sm text-slate-500">
                          Période: {format(new Date(selectedInvoice.year, selectedInvoice.month - 1), 'MMMM yyyy', { locale: fr })}
                        </p>
                      </td>
                      <td className="text-center py-3 font-mono">{selectedInvoice.total_days} j</td>
                      <td className="text-right py-3 font-mono">{formatCurrency(selectedInvoice.tjm)}</td>
                      <td className="text-right py-3 font-mono font-medium">{formatCurrency(selectedInvoice.amount_ht)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Total HT</span>
                      <span className="font-mono">{formatCurrency(selectedInvoice.amount_ht)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">TVA ({selectedInvoice.vat_rate}%)</span>
                      <span className="font-mono">{formatCurrency(selectedInvoice.amount_ttc - selectedInvoice.amount_ht)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t-2 border-slate-900">
                      <span className="font-bold text-slate-900">Total TTC</span>
                      <span className="font-mono font-bold text-lg">{formatCurrency(selectedInvoice.amount_ttc)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Terms */}
                <div className="mt-8 pt-4 border-t border-slate-200 text-sm text-slate-500">
                  <p className="font-medium text-slate-700 mb-1">Conditions de paiement</p>
                  <p>{selectedInvoice.payment_terms}</p>
                  <p className="mt-2">IBAN: FR76 1234 5678 9012 3456 7890 123</p>
                  <p>BIC: BNPAFRPP</p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>
                Fermer
              </Button>
              <Button 
                className="bg-slate-900 hover:bg-slate-800" 
                data-testid="download-invoice-btn"
                onClick={() => {
                  const apiUrl = process.env.REACT_APP_BACKEND_URL;
                  const token = localStorage.getItem('token');
                  window.open(`${apiUrl}/api/invoices/${selectedInvoice.invoice_id}/pdf?token=${token}`, '_blank');
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger PDF
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
